import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useProject } from "../../layout/ProjectLayout";
import { RagDot, KpiCard, StatusBadge, PageHeader } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sparkles, AlertTriangle } from "lucide-react";

export default function Dashboard() {
  const { projectId } = useParams();
  const { project, reloadProject } = useProject();
  const [data, setData] = useState(null);
  const [work, setWork] = useState(null);
  const [obOpen, setObOpen] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [obBusy, setObBusy] = useState(false);
  const [healthBusy, setHealthBusy] = useState(false);
  const [health, setHealth] = useState(null);

  const computeHealth = async () => {
    setHealthBusy(true);
    try {
      const { data: h } = await api.post(`/projects/${projectId}/health-score`);
      setHealth(h);
      toast.success(`Health score: ${h.healthScore} (${h.healthGrade})`);
    } catch (e) { toast.error(errMsg(e)); }
    setHealthBusy(false);
  };

  useEffect(() => {
    api.get(`/projects/${projectId}/dashboard`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/my-work`).then((r) => setWork(r.data)).catch(() => {});
  }, [projectId]);

  const genQuestions = async () => {
    setObBusy(true);
    try {
      const { data: d } = await api.post(`/projects/${projectId}/ai/generate`, { generationType: "onboarding" });
      setQuestions(Array.isArray(d.result) ? d.result : []);
    } catch (e) { toast.error(errMsg(e)); }
    setObBusy(false);
  };

  const saveContext = async () => {
    const ctx = questions.map((q) => ({ question: q.question, answer: answers[q.key || q.question] || "" }));
    try {
      await api.patch(`/projects/${projectId}`, { projectContext: ctx });
      toast.success("Project Context saved — the AI assistant will use it from now on");
      setObOpen(false); reloadProject && reloadProject();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;
  const p = { ...data.project, ...(health || {}) };

  return (
    <div data-testid="project-dashboard">
      <PageHeader overline={`${p.code || ""} · ${p.primaryMethodology === "Pmbok" ? "PMBOK" : p.primaryMethodology} · ${p.status}`} title={p.name}
        actions={
          <Button data-testid="setup-context-btn" variant="outline" className="rounded-none border-[#1E6FE8] text-[#1E6FE8]"
            onClick={() => { setObOpen(true); if (!questions) genQuestions(); }}>
            <Sparkles className="h-4 w-4 mr-1" />{p.projectContext ? "Update AI Context" : "Set up AI Project Context"}
          </Button>
        } />

      {!p.projectContext && (
        <div data-testid="onboarding-banner" className="border border-[#1E6FE8]/30 bg-[#1E6FE8]/5 p-3 mb-4 text-sm">
          <span className="font-semibold text-[#1E6FE8]">AI onboarding: </span>
          Answer a few questions so the assistant understands this project's drivers, constraints and success criteria.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="border border-border bg-card p-4" data-testid="health-score-card">
          <div className="flex items-center justify-between">
            <div className="overline-label">AI Health Score</div>
            <button data-testid="compute-health-btn" onClick={computeHealth} disabled={healthBusy}
              className="text-[10px] uppercase tracking-wide text-[#1E6FE8] underline disabled:opacity-50">
              {healthBusy ? "Scoring…" : p.healthScore != null ? "Refresh" : "Compute"}
            </button>
          </div>
          {p.healthScore != null ? (
            <div className="mt-1 flex items-baseline gap-2">
              <span data-testid="health-score-value" className={`font-display font-black text-3xl tracking-tighter ${p.healthScore >= 70 ? "text-emerald-600" : p.healthScore >= 55 ? "text-amber-600" : "text-red-600"}`}>{p.healthScore}</span>
              <span className="text-sm font-bold">{p.healthGrade}</span>
            </div>
          ) : <div className="mt-2 text-xs text-muted-foreground">Not scored yet — blends RAG, slippage & risks.</div>}
          {p.healthSummary && <p data-testid="health-summary" className="text-[11px] text-muted-foreground mt-1 leading-snug">{p.healthSummary}</p>}
        </div>
        {[["Schedule", p.scheduleRag], ["Budget", p.budgetRag], ["Scope / Quality", p.scopeQualityRag]].map(([label, rag]) => (
          <div key={label} className="border border-border bg-card p-4">
            <div className="overline-label">{label}</div>
            <div className="mt-2"><RagDot value={rag} /></div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard testId="kpi-tasks" label="Tasks" value={data.taskStats.total} sub={`${data.taskStats.avgPercentComplete}% avg complete · ${data.taskStats.critical} critical`} />
        <KpiCard testId="kpi-risks" label="Open Risks / Issues" value={`${data.openRisks} / ${data.openIssues}`} sub="From RAID log" />
        <KpiCard testId="kpi-crs" label="Open Change Requests" value={data.openChangeRequests} sub="Change management" />
        <KpiCard testId="kpi-golive" label="Go-Live Checklist" value={`${data.goLive.complete}/${data.goLive.total}`} sub={`Requirements agreed: ${data.requirements.agreed}/${data.requirements.total}`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="border border-border bg-card" data-testid="my-work-panel">
          <div className="px-4 py-2.5 border-b border-border overline-label">My Work</div>
          <div className="p-4 space-y-3 text-sm max-h-72 overflow-y-auto">
            {work && (
              <>
                <div>
                  <div className="text-xs font-bold text-red-600 uppercase tracking-wide mb-1">Overdue ({work.overdue.length})</div>
                  {work.overdue.map((t) => <div key={t.id} className="border-l-2 border-[#FF3B30] pl-2 py-0.5">{t.wbsCode} {t.name} <span className="text-xs text-muted-foreground">due {t.plannedFinish}</span></div>)}
                  {work.overdue.length === 0 && <span className="text-xs text-muted-foreground">Nothing overdue.</span>}
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide mb-1">Upcoming 14 days ({work.upcoming.length})</div>
                  {work.upcoming.map((t) => <div key={t.id} className="border-l-2 border-[#1E6FE8] pl-2 py-0.5">{t.wbsCode} {t.name} <span className="text-xs text-muted-foreground">due {t.plannedFinish}</span></div>)}
                  {work.upcoming.length === 0 && <span className="text-xs text-muted-foreground">No upcoming assignments.</span>}
                </div>
                <div>
                  <div className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-1">Awaiting my review / approval ({work.approvals.length})</div>
                  {work.approvals.map((t) => <div key={t.id} className="border-l-2 border-[#FFB300] pl-2 py-0.5">{t.wbsCode} {t.name}</div>)}
                  {work.approvals.length === 0 && <span className="text-xs text-muted-foreground">No pending approvals.</span>}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="border border-border bg-card" data-testid="escalations-panel">
          <div className="px-4 py-2.5 border-b border-border overline-label flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-[#FF3B30]" /> Escalations (role-based)</div>
          <div className="p-4 space-y-2 text-sm max-h-72 overflow-y-auto">
            {work?.escalations?.length ? work.escalations.map((e) => (
              <div key={e.taskId} className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-2">
                <div className="flex justify-between"><span className="font-medium">{e.wbsCode} {e.name}</span><StatusBadge value={`${e.daysOverdue}d overdue`} tone="red" /></div>
                <div className="text-xs text-muted-foreground mt-1">Escalated to: {e.escalateTo.map((x) => `${x.name} (${x.role})`).join(", ") || "—"}</div>
              </div>
            )) : <span className="text-xs text-muted-foreground">No overdue items — nothing to escalate.</span>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-border bg-card">
          <div className="px-4 py-2.5 border-b border-border overline-label">Summary Milestones</div>
          <table className="w-full text-sm data-table">
            <tbody>
              {(data.milestones || []).map((m, i) => (
                <tr key={i}>
                  <td className="font-medium">{m.name}</td>
                  <td className="text-muted-foreground whitespace-nowrap">{m.targetDate}</td>
                  <td><StatusBadge value={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border border-border bg-card">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="overline-label">Top Open Risks (High)</span>
            <Link to={`/projects/${projectId}/raid_items`} className="text-xs underline text-[#1E6FE8]">RAID log</Link>
          </div>
          <table className="w-full text-sm data-table">
            <tbody>
              {data.topRisks.length === 0 && <tr><td className="text-muted-foreground py-4">No open high risks.</td></tr>}
              {data.topRisks.map((r) => (
                <tr key={r.id}>
                  <td>{r.description}</td>
                  <td className="whitespace-nowrap"><StatusBadge value={r.priority} /></td>
                  <td className="text-muted-foreground whitespace-nowrap">{r.owner}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-4">
        <KpiCard testId="kpi-finance" label="Finance Rollup" value={`$${Number(data.finance?.actual || 0).toLocaleString()} / $${Number(data.finance?.budget || 0).toLocaleString()}`} sub={`Forecast $${Number(data.finance?.forecast || 0).toLocaleString()} · from Finance module`} />
        <KpiCard testId="kpi-scorecard" label="Scorecard" value={`${data.scorecard?.green || 0}G · ${data.scorecard?.amber || 0}A · ${data.scorecard?.red || 0}R`} sub={`${data.scorecard?.total || 0} metrics tracked`} />
        <KpiCard label="Planned Budget" value={p.plannedBudget ? `$${Number(p.plannedBudget).toLocaleString()}` : "—"} sub={p.targetGoLiveDate ? `Target go-live ${p.targetGoLiveDate}` : ""} />
      </div>

      <Dialog open={obOpen} onOpenChange={setObOpen}>
        <DialogContent className="rounded-none max-w-xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">AI Project Context Onboarding</DialogTitle></DialogHeader>
          {obBusy && <p className="text-sm text-muted-foreground">Generating onboarding questions…</p>}
          {questions && questions.map((q, i) => (
            <div key={i}>
              <label className="text-xs font-semibold">{q.question}</label>
              {q.hint && <p className="text-[11px] text-muted-foreground">{q.hint}</p>}
              <Textarea data-testid={`onboarding-answer-${i}`} rows={2} value={answers[q.key || q.question] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.key || q.question]: e.target.value })} />
            </div>
          ))}
          {questions && <Button data-testid="save-context-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={saveContext}>Save Project Context</Button>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
